// Reads the appearance columns from a widget_config (or template) record and
// returns a style object suitable for spreading into a React element's `style`
// prop. Any null/undefined column falls back to the Opendate default.
//
// Currently invoked only when widgetType === 'custom' so club/festival widgets
// render exactly as before. Future PRs may extend coverage to all widget types.

import deriveInteractiveColors from './deriveInteractiveColors';

export const DEFAULTS = {
  // Simple (legacy) — also serve as defaults for the Advanced subset they map to.
  background_color: '#ffffff',
  button_color:     '#FF595E',
  font_color:       '#333333',
  card_color:       '#ffffff',
  border_color:     '#eeeeee',
  font_family:      'Open Sans, sans-serif',
  corner_radius:    6,

  // Advanced.
  cart_background_color:           '#fafafa',
  cart_border_color:               '#eeeeee',
  promo_background_color:          '#ffffff',
  promo_border_color:              '#e8e8e8',
  promo_text_color:                '#333333',
  promo_placeholder_color:         '#aaaaaa',
  tab_active_color:                '#1982C4',
  tab_inactive_color:              '#999999',
  tab_border_color:                '#eeeeee',
  body_text_color:                 '#555555',
  secondary_text_color:            '#888888',
  link_color:                      '#333333',
  quantity_stepper_border_color:   '#414651',
  quantity_stepper_fill_color:     '#ffffff',
  quantity_stepper_icon_color:     '#414651',
};

const pick = (widgetConfig, key) => {
  const value = widgetConfig && widgetConfig[key];
  return (value === null || value === undefined) ? DEFAULTS[key] : value;
};

const applyWidgetSettings = (widgetConfig = {}) => {
  // Simple (and inheritance roots).
  const bg     = pick(widgetConfig, 'background_color');
  const button = pick(widgetConfig, 'button_color');
  const font   = pick(widgetConfig, 'font_color');
  const card   = pick(widgetConfig, 'card_color');
  const border = pick(widgetConfig, 'border_color');
  const family = pick(widgetConfig, 'font_family');
  const radius = pick(widgetConfig, 'corner_radius');

  // Advanced.
  const cartBg          = pick(widgetConfig, 'cart_background_color');
  const cartBorder      = pick(widgetConfig, 'cart_border_color');
  const promoBg         = pick(widgetConfig, 'promo_background_color');
  const promoBorder     = pick(widgetConfig, 'promo_border_color');
  const promoText       = pick(widgetConfig, 'promo_text_color');
  const promoPlace      = pick(widgetConfig, 'promo_placeholder_color');
  const tabActive       = pick(widgetConfig, 'tab_active_color');
  const tabInactive     = pick(widgetConfig, 'tab_inactive_color');
  const tabBorder       = pick(widgetConfig, 'tab_border_color');
  const bodyText        = pick(widgetConfig, 'body_text_color');
  const secondaryText   = pick(widgetConfig, 'secondary_text_color');
  const link            = pick(widgetConfig, 'link_color');
  const stepperBorder   = pick(widgetConfig, 'quantity_stepper_border_color');
  const stepperFill     = pick(widgetConfig, 'quantity_stepper_fill_color');
  const stepperIcon     = pick(widgetConfig, 'quantity_stepper_icon_color');

  const buttonInteractive      = deriveInteractiveColors(button);
  const linkInteractive        = deriveInteractiveColors(link);
  const tabActiveInteractive   = deriveInteractiveColors(tabActive);
  const tabInactiveInteractive = deriveInteractiveColors(tabInactive);

  return {
    // Simple variables — kept for backwards-compat consumers.
    '--widget-bg-color':            bg,
    '--widget-button-color':        button,
    '--widget-button-hover-color':  buttonInteractive.hover,
    '--widget-button-text-color':   buttonInteractive.text,
    '--widget-font-color':          font,
    '--widget-card-color':    card,
    '--widget-border-color':  border,
    '--widget-font-family':   family,
    '--widget-corner-radius': `${radius}px`,

    // Advanced variables.
    '--widget-cart-bg-color':                cartBg,
    '--widget-cart-border-color':            cartBorder,
    '--widget-promo-bg-color':               promoBg,
    '--widget-promo-border-color':           promoBorder,
    '--widget-promo-text-color':             promoText,
    '--widget-promo-placeholder-color':      promoPlace,
    '--widget-tab-active-color':             tabActive,
    '--widget-tab-active-hover-color':       tabActiveInteractive.hover,
    '--widget-tab-inactive-color':           tabInactive,
    '--widget-tab-inactive-hover-color':     tabInactiveInteractive.hover,
    '--widget-tab-border-color':             tabBorder,
    '--widget-body-text-color':              bodyText,
    '--widget-secondary-text-color':         secondaryText,
    '--widget-link-color':                   link,
    '--widget-link-hover-color':             linkInteractive.hover,
    '--widget-quantity-stepper-border-color': stepperBorder,
    '--widget-quantity-stepper-fill-color':   stepperFill,
    '--widget-quantity-stepper-icon-color':   stepperIcon,

    fontFamily: family,
    color:      font,
    background: bg
  };
};

export default applyWidgetSettings;
